import React, { useState, useCallback, useMemo } from 'react';
import { xcss, Pressable, Inline } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import commonMessages from '../common/ui/messages.tsx';
import FlyoutContent from '../common/ui/flyout-content/index.tsx';
import FieldBase from '../common/ui/index.tsx';
import type { Props } from './types.tsx';
import messages from './messages.tsx';

const TeamCell = ({
	fieldKey,
	values,
	groupName,
	totalIssues,
	unassignedCount,
	groupByLabel,
	swimlane,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const onToggle = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen]);

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const label = useMemo(
		() =>
			fg('jira-issue-terminology-refresh-m3')
				? commonMessages.issueCountLabelIssueTermRefresh
				: commonMessages.issueCountLabel,
		[],
	);

	const content = useMemo(() => {
		const valuesWithUnassigned = unassignedCount
			? [
					...values,
					{ title: formatMessage(messages.unassigned), count: unassignedCount, avatarUrl: '' },
				]
			: values;

		return valuesWithUnassigned.map(({ title, avatarUrl, count }) => ({
			title: (
				<Inline space="space.050" alignBlock="center">
					<Inline
						as="span"
						alignBlock="center"
						alignInline="center"
						xcss={[iconWrapperStyles, !avatarUrl && defaultIconWrapperStyles]}
					>
						{avatarUrl ? <Image src={avatarUrl} alt={title} /> : <PeopleGroupIcon label={title} />}
					</Inline>
					{title}
				</Inline>
			),
			count,
		}));
	}, [values, formatMessage, unassignedCount]);

	const renderContent = useCallback(
		() => (
			<FlyoutContent
				fieldName={commonMessages.teamTitle}
				label={label}
				content={content}
				total={totalIssues}
				testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.group.aggregate-field.team-cell.team-flyout"
				ariaLabel={formatMessage(messages.ariaLabel, {
					count: content.length,
					groupByLabel,
					swimlane,
				})}
			/>
		),
		[content, label, totalIssues, groupByLabel, swimlane, formatMessage],
	);

	const renderTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<FieldBase
				component={Pressable}
				onClick={onToggle}
				triggerProps={triggerProps}
				tabIndex={0}
				testId={`portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.group.aggregate-field.team-cell.${groupName}.${fieldKey}`}
				message={messages.numberOfTeams}
				messageValues={{ count: values.length }}
				fieldKey={fieldKey}
			/>
		),
		[onToggle, values.length, groupName, fieldKey],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			trigger={renderTrigger}
			content={renderContent}
			placement="bottom-start"
		/>
	);
};

export default TeamCell;

const iconWrapperStyles = xcss({
	width: '24px',
	height: '24px',
	marginRight: 'space.050',
});

const defaultIconWrapperStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: '50%',
});
